import { ref, type Ref } from 'vue'
import $axios from '@/api/apiConnection'

interface QueueMemberExtension {
    ExtensionID: number
    IsLoginRequired: 0 | 1
}

interface ApiQueueMember {
    UserID: number
    UserName: string
    Extensions: QueueMemberExtension[]
}

interface ApiQueueItem {
    QueueID: number
    QueueName: string
    Members: ApiQueueMember[]
}

interface DataQueues {
    Queues: ApiQueueItem[]
}

interface QueuesListResponseData {
    Data: DataQueues
}

export interface StoredQueueMember {
    UserID: number
    UserName: string
    IsLoginRequired: boolean
}

export interface StoredQueueItem {
    QueueID: number
    QueueName: string
    Members: Record<number, StoredQueueMember>
}

const FETCH_DELAY_MS = 60_000 * 5 // 5 min

class QueueAgentsDataServiceClass {
    private trackedUids = new Set<string>()
    private timeoutId: ReturnType<typeof setTimeout> | null = null
    private fetchGeneration = 0

    public data: Ref<Record<number, StoredQueueItem>> = ref({})
    public allMembers:Ref<StoredQueueMember[]>  = ref([])

    startTracking (uid: string): void {
        const wasEmpty = this.trackedUids.size === 0
        this.trackedUids.add(uid)

        if (wasEmpty) {
            this.fetchGeneration++
            this.clearScheduledFetch()
            this.fetchAndSchedule(this.fetchGeneration)
        }
    }

    stopTracking (uid: string): void {
        this.trackedUids.delete(uid)

        if (this.trackedUids.size === 0) {
            this.fetchGeneration++
            this.clearScheduledFetch()
            this.data.value = {}
        }
    }

    private clearScheduledFetch (): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
    }

    private scheduleNextFetch (generation: number): void {
        if (generation !== this.fetchGeneration || this.trackedUids.size === 0) {
            return
        }

        this.timeoutId = setTimeout(() => {
            this.timeoutId = null
            this.fetchAndSchedule(generation)
        }, FETCH_DELAY_MS)
    }

    private async fetchAndSchedule (generation: number): Promise<void> {
        if (generation !== this.fetchGeneration || this.trackedUids.size === 0) {
            return
        }

        try {
            const response = await $axios.get('/TeamStatistics/AccountQueues/', {}) as QueuesListResponseData

            if (generation !== this.fetchGeneration) {
                return
            }

            const queues = response?.Data?.Queues
            if (!queues) {
                this.data.value = {}
                return
            }


            const allMembers: Record<number, StoredQueueMember> = {}

            this.data.value = queues.reduce<Record<number, StoredQueueItem>>((acc, queue) => {
                const members = queue.Members.reduce<Record<number, StoredQueueMember>>((membersAcc, m) => {
                    const hasExtensionId = m.Extensions.some(e => !!e.ExtensionID)

                    if (!hasExtensionId) return membersAcc

                    const loginRequired = m.Extensions.some(e => e.IsLoginRequired === 1)

                    membersAcc[m.UserID] = {
                        UserID: m.UserID,
                        UserName: m.UserName,
                        IsLoginRequired: loginRequired,
                    }

                    allMembers[m.UserID] = {
                        UserID: m.UserID,
                        UserName: m.UserName,
                        IsLoginRequired: loginRequired
                    }
                    return membersAcc
                }, {})

                if (Object.keys(members).length) {
                    acc[queue.QueueID] = {
                        QueueID: queue.QueueID,
                        QueueName: queue.QueueName,
                        Members: members
                    }
                }

                return acc
            }, {})
            this.allMembers.value = Object.values(allMembers)
        } catch (e) {
            if (generation !== this.fetchGeneration) return
            console.error('[QueueAgentsDataService] fetchData failed:', e)
        } finally {
            this.scheduleNextFetch(generation)
        }
    }
}

const QueueAgentsDataService = new QueueAgentsDataServiceClass()

export default QueueAgentsDataService
